import { useTranslation } from 'react-i18next';
import { CalendarDays, FilePlus2, FileText, FolderKanban, Repeat } from 'lucide-react';
import { isOpen, type TaskPriority, type TaskStatus } from '@nexus/core';
import { toggleDone, updateTask, withDueTime, type Task } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { useUi } from '../../state/ui';
import { promoteTask } from './promote';
import { cn } from '../../lib/cn';
import {
  displayTitle,
  dueTone,
  formatDue,
  PRIORITY_ICON,
  PRIORITY_ORDER,
  STATUSES,
} from './format';
import { openRow } from '../../lib/peek';

const toneClass = {
  overdue: 'bg-red-500/15 text-danger',
  today: 'bg-amber-500/20 text-fg',
  soon: 'bg-bg-subtle text-fg',
  later: 'bg-bg-subtle text-fg-muted',
};

export function TaskRow({
  task,
  projectTitle,
  showSource = true,
  showStatus = false,
  compact = false,
  readOnly = false,
}: {
  task: Task;
  projectTitle?: string | null;
  showSource?: boolean;
  showStatus?: boolean;
  compact?: boolean;
  /** A read-only workspace membership: no checkbox, no editing, no promote. */
  readOnly?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const done = !isOpen(task.status);
  // A task page opens in a pop-up (like a database row); a checkbox task on its page.
  const openTask = (newTab: boolean) =>
    task.kind === 'page'
      ? openRow(task.pageId, { ctrlKey: newTab })
      : open({ kind: 'page', pageId: task.pageId }, newTab);
  const chip = 'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs';

  const title = (
    // A non-draggable link (not a button): dragging from the title moves the whole card.
    <a
      href={`#/p/${task.pageId}`}
      draggable={false}
      title={task.title}
      onClick={(e) => {
        e.preventDefault();
        openTask(e.ctrlKey || e.metaKey);
      }}
      className={cn(
        'text-left text-sm hover:underline',
        !compact && 'block min-w-0 truncate',
        done && 'text-fg-muted line-through',
        task.status === 'doing' && 'font-medium',
      )}
    >
      {displayTitle(task.title) || t('page.untitled')}
    </a>
  );
  const checkbox = (
    <input
      type="checkbox"
      checked={task.status === 'done'}
      aria-label={t('tasks.toggle', { title: task.title })}
      disabled={readOnly}
      onChange={() => void toggleDone(store, task)}
      className={cn('nx-check shrink-0', compact && 'mt-0.5')}
    />
  );

  // Lists: one line, with the details in fixed columns so they line up from row to
  // row; cards: the details below the title.
  return compact ? (
    <div
      className="group flex items-start gap-2 rounded-md px-2 py-1 hover:bg-bg-hover"
      data-testid="task-row"
    >
      {checkbox}
      <div className="min-w-0 flex-1">
        {title}
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
          {due()}
          {task.priority && priority()}
          {showStatus && status()}
          {extras()}
          {place()}
        </div>
      </div>
    </div>
  ) : (
    <div
      className="group flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-2 py-1.5 hover:bg-bg-hover sm:flex-nowrap"
      data-testid="task-row"
    >
      {checkbox}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {title}
        <span className="flex shrink-0 items-center gap-1.5 text-xs text-fg-muted">{extras()}</span>
      </div>
      <div className="flex basis-full items-center gap-2 pl-7 text-xs text-fg-muted sm:basis-auto sm:pl-0">
        <span className="w-32 shrink-0">{due()}</span>
        <span className="w-28 shrink-0">{priority()}</span>
        {showStatus && <span className="w-28 shrink-0">{status()}</span>}
        <span className="w-36 shrink-0 truncate">{place()}</span>
      </div>
    </div>
  );

  function due() {
    if (task.due)
      return readOnly ? (
        <span className={cn(chip, !done && toneClass[dueTone(task.due)])}>
          <CalendarDays className="size-3" />
          {formatDue(task.due, i18n.language, t, undefined, task.dueTime)}
        </span>
      ) : (
        <label
          className={cn(chip, 'relative cursor-pointer', !done && toneClass[dueTone(task.due)])}
        >
          <CalendarDays className="size-3" />
          {formatDue(task.due, i18n.language, t, undefined, task.dueTime)}
          <input
            type="date"
            value={task.due}
            aria-label={t('tasks.due')}
            onChange={(e) =>
              void updateTask(store, task, { due: withDueTime(e.target.value || null, task) })
            }
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </label>
      );
    // Hover-only controls are left out of compact cards: they would catch drags.
    if (done || compact || readOnly) return null;
    return (
      <label
        className={cn(
          chip,
          'relative cursor-pointer opacity-0 group-hover:opacity-100 focus-within:opacity-100',
        )}
      >
        <CalendarDays className="size-3" />
        {t('tasks.addDue')}
        <input
          type="date"
          aria-label={t('tasks.due')}
          onChange={(e) => e.target.value && void updateTask(store, task, { due: e.target.value })}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
      </label>
    );
  }

  function priority() {
    if (readOnly) {
      if (!task.priority) return null;
      return (
        <span className={cn(chip, 'max-w-full')}>
          {PRIORITY_ICON[task.priority]} {t(`tasks.priorities.${task.priority}`)}
        </span>
      );
    }
    return (
      <select
        value={task.priority ?? ''}
        aria-label={t('tasks.priority')}
        onChange={(e) =>
          void updateTask(store, task, {
            priority: (e.target.value || null) as TaskPriority | null,
          })
        }
        className={cn(
          'max-w-full rounded bg-transparent text-xs',
          !task.priority && 'opacity-0 group-hover:opacity-100 focus:opacity-100',
        )}
      >
        <option value="">{t('tasks.noPriority')}</option>
        {PRIORITY_ORDER.map((p) => (
          <option key={p} value={p}>
            {PRIORITY_ICON[p]} {t(`tasks.priorities.${p}`)}
          </option>
        ))}
      </select>
    );
  }

  function status() {
    if (readOnly)
      return (
        <span className="max-w-full rounded-full bg-bg-subtle px-2 py-0.5 text-xs">
          {t(`tasks.statuses.${task.status}`)}
        </span>
      );
    return (
      <select
        value={task.status}
        aria-label={t('tasks.status')}
        onChange={(e) => void updateTask(store, task, { status: e.target.value as TaskStatus })}
        className="max-w-full rounded-full bg-bg-subtle px-2 py-0.5 text-xs"
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {t(`tasks.statuses.${s}`)}
          </option>
        ))}
      </select>
    );
  }

  /** Where the task belongs: its project, else the page it is on. */
  function place() {
    if (projectTitle && task.projectId)
      return (
        <button
          type="button"
          className={cn(chip, 'max-w-full hover:text-fg')}
          onClick={() => open({ kind: 'page', pageId: task.projectId! })}
        >
          <FolderKanban className="size-3 shrink-0" />
          <span className="truncate">{projectTitle}</span>
        </button>
      );
    if (showSource && task.kind === 'inline' && task.sourceTitle && task.pageId !== task.projectId)
      return (
        <span className={cn(chip, 'max-w-full')}>
          <FileText className="size-3 shrink-0" />
          <span className="truncate">{task.sourceTitle}</span>
        </span>
      );
    return null;
  }

  /** Next to the title: repeat, tags, and (on hover) "make task page". */
  function extras() {
    return (
      <>
        {task.recurrence && (
          <span className={chip} title={task.recurrence}>
            <Repeat className="size-3" />
          </span>
        )}
        {task.tags.map((tag) => (
          <button
            key={tag}
            type="button"
            className="nx-tag"
            onClick={() => open({ kind: 'tag', tag })}
          >
            #{tag}
          </button>
        ))}
        {task.kind === 'inline' && !compact && !readOnly && (
          <button
            type="button"
            title={t('tasks.promoteHint')}
            onClick={() => void promoteTask(store, task)}
            className={cn(
              chip,
              'hover:text-fg [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus:opacity-100',
            )}
          >
            <FilePlus2 className="size-3" />
            {t('tasks.promote')}
          </button>
        )}
      </>
    );
  }
}
